// Controls joined into one field: a search box with a filter menu, an input with a button.
import { I80Element, define, h } from "../base.js";
import { field } from "../field.js";

const TEXT_INPUT = "i80-form-text-input i80-typography-body-200 i80-font-weight-regular";
const SELECT = "i80-form-select i80-typography-body-200 i80-font-weight-regular";
const WRAPPED = ["i80-button", "i80-text-input", "i80-select", "i80-textarea"];

/**
 * <i80-segmented-group label="New API key" helper-text="At most five keys">
 *   <input type="text" name="key">
 *   <button type="submit">Generate</button>
 * </i80-segmented-group>
 *
 * The segments are the native <input>, <select>, <button> and <a> you write inside: the
 * group moves them into one row and adds the i80 classes, so names, values, form and
 * onclick are yours and a server-rendered form needs no JavaScript to look right. A
 * <i80-dropdown> child is a segment too, for a filter menu.
 *
 * With a label the group renders inside a form field (label, helper text, error), linked
 * to the first control in the row.
 */
export class I80SegmentedGroup extends I80Element {
  static get observedAttributes() {
    return ["full-width", "max-width", "label", "helper-text", "error", "invalid", "required", "optional"];
  }

  setup() {
    let group = this.part("group");
    if (!group) {
      group = h("div", { class: "i80-segmented-group", "data-i80-part": "group" }, this.takeContent());
      this.append(group);
    }
    this._group = group;
    // A label means the group is a form control, so it gets the same field markup as the
    // other form elements, tied to the first control in the row.
    const control = ["label", "helper-text", "error"].some((a) => this.hasAttribute(a))
      && group.querySelector("input, select, textarea, button, a");
    if (control) this._render = field(this, control, { controlWrapper: group });
  }

  adopt() {
    this._group.append(...this.takeContent());
  }

  update() {
    const invalid = this._render ? this._render().invalid : this.flag("invalid");
    this._group.className = [
      "i80-segmented-group",
      this.flag("full-width") && "i80-segmented-group--is-full-width",
    ].filter(Boolean).join(" ");
    this._group.style.setProperty("--i80-segmented-group-max-width", this.getAttribute("max-width") ?? "");
    for (const segment of this._group.children) this._segment(segment, invalid);
  }

  /** Style one segment. Which corners are rounded is components.css's business, not ours. */
  _segment(el, invalid) {
    switch (el.localName) {
      case "input":
        el.className = [TEXT_INPUT, invalid && "i80-form-text-input--is-invalid"].filter(Boolean).join(" ");
        break;
      case "select":
        el.className = [SELECT, invalid && "i80-form-select--is-invalid"].filter(Boolean).join(" ");
        break;
      case "button":
      case "a":
        this._button(el);
        break;
      case "i80-dropdown":
        // components.css joins the segments through `.i80-segmented-group > .i80-dropdown`,
        // and the <div> the tag renders is one level too deep for that, so the tag itself
        // carries the class (and a box of its own, see the host rules in index.js).
        el.classList.add("i80-dropdown");
        break;
      default:
        if (WRAPPED.includes(el.localName)) {
          console.warn(`<i80-segmented-group>: write a native <button> or <input> inside, not <${el.localName}>; the group styles and joins those itself.`);
        }
    }
  }

  _button(el) {
    const modifiers = new Set([...el.classList].filter((c) => c.startsWith("i80-button--")));
    const has = (kind) => [...modifiers].some((c) => c.startsWith(`i80-button--${kind}-`));
    if (!has("color")) modifiers.add("i80-button--color-secondary");
    if (!has("size")) modifiers.add("i80-button--size-medium");
    el.className = ["i80-button", ...modifiers].join(" ");
    if (el.querySelector(":scope > .i80-button__text")) return;
    const iconEl = el.querySelector(":scope > i80-icon");
    const rest = [...el.childNodes].filter((n) => n !== iconEl);
    el.replaceChildren(
      ...(iconEl ? [h("span", { class: "i80-button__icon" }, iconEl)] : []),
      h("span", { class: "i80-button__text" }, rest));
  }
}

define("i80-segmented-group", I80SegmentedGroup);
